
import React, { useState, useEffect, useContext, Suspense, useRef } from 'react';
import { ImageEdits, PostContext, DEFAULT_THEME_COLOR, Timestamp, getSafeImageEdits, Page, PageContextType } from '../types';
import { Timestamp as TimestampValue } from '../firebase';
import { useAuth } from '../AuthContext';
import { PageContext } from '../PageContext';
import AIImageGeneratorModal from '../components/AIImageGeneratorModal';
import BackButton from '../components/BackButton';

// Lazy load PostCard to break circular dependency with App.tsx
const PostCard = React.lazy(() => import('../components/PostCard'));

interface ImageEditorPageProps {
    imageUrl: string;
    currentEdits: ImageEdits;
    previewShape: 'circle' | 'square';
    onSave: (newEdits: ImageEdits, newPostColor?: string) => void;
    postContext?: PostContext;
}

const AdjustmentSlider: React.FC<{ label: string, value: number, onChange: (value: number) => void, min: number, max: number, step?: number }> = ({ label, value, onChange, min, max, step = 1 }) => ( <div><label className="block text-sm font-medium text-gray-300">{label}</label><div className="flex items-center space-x-2"><input type="range" min={min} max={max} step={step} value={value} onChange={(e) => onChange(parseFloat(e.target.value))} className="w-full color-slider" /><span className="text-sm font-mono w-12 text-center text-gray-200">{value}</span></div></div> );

const ImageEditorPage: React.FC<ImageEditorPageProps> = ({ imageUrl, currentEdits, previewShape, onSave, postContext }) => {
    const { userProfile } = useAuth();
    const pageContext = useContext<PageContextType | undefined>(PageContext);
    const [edits, setEdits] = useState<ImageEdits>(getSafeImageEdits(currentEdits));
    
    // AI Integration State
    const [localImageUrl, setLocalImageUrl] = useState(imageUrl);
    const [isAIModalOpen, setAIModalOpen] = useState(false);

    // Zoom Handling Refs
    const interactionRef = useRef({
        startDist: 0,
        startScale: 1,
        startX: 0,
        startY: 0,
        startPanX: 0,
        startPanY: 0,
        isPinching: false,
        isPanning: false
    });

    useEffect(() => {
        setEdits(getSafeImageEdits(currentEdits));
        setLocalImageUrl(imageUrl);
    }, [currentEdits, imageUrl]);

    const handleSave = () => {
        // Pass back the edits AND the image URL (in case it was changed via AI)
        onSave(edits, localImageUrl);
        pageContext?.goBack();
    };
    
    const resetAdjustments = () => { setEdits(prev => ({ ...prev, adjustments: { brightness: 100, contrast: 100, saturate: 100, blur: 0, zoom: 1, offsetX: 0, offsetY: 0, } })); };

    const handleAIImageGenerated = (base64: string) => {
        const dataUrl = `data:image/jpeg;base64,${base64}`;
        setLocalImageUrl(dataUrl);
        setAIModalOpen(false);
        setEdits(prev => ({ ...prev, adjustments: { ...prev.adjustments, zoom: 1, offsetX: 0, offsetY: 0 }}));
    };

    // Pinch-to-zoom handlers
    const handleTouchStart = (e: React.TouchEvent) => {
        if (e.touches.length === 2) {
            interactionRef.current.isPinching = true;
            interactionRef.current.startDist = Math.hypot(
                e.touches[0].clientX - e.touches[1].clientX,
                e.touches[0].clientY - e.touches[1].clientY
            );
            interactionRef.current.startScale = edits.adjustments.zoom;
        } else if (e.touches.length === 1) {
            interactionRef.current.isPanning = true;
            interactionRef.current.startX = e.touches[0].clientX;
            interactionRef.current.startY = e.touches[0].clientY;
            interactionRef.current.startPanX = edits.adjustments.offsetX;
            interactionRef.current.startPanY = edits.adjustments.offsetY;
        }
    };

    const handleTouchMove = (e: React.TouchEvent) => {
        if (interactionRef.current.isPinching && e.touches.length === 2) {
            e.preventDefault();
            const dist = Math.hypot(
                e.touches[0].clientX - e.touches[1].clientX,
                e.touches[0].clientY - e.touches[1].clientY
            );
            const newScale = Math.min(Math.max(0.5, interactionRef.current.startScale * (dist / interactionRef.current.startDist)), 4);
            setEdits(prev => ({ ...prev, adjustments: { ...prev.adjustments, zoom: newScale }}));
        } else if (interactionRef.current.isPanning && e.touches.length === 1 && edits.adjustments.zoom > 1) {
            // Only pan if zoomed in
            e.preventDefault();
            const dx = e.touches[0].clientX - interactionRef.current.startX;
            const dy = e.touches[0].clientY - interactionRef.current.startY;
            setEdits(prev => ({ 
                ...prev, 
                adjustments: {
                    ...prev.adjustments,
                    offsetX: interactionRef.current.startPanX + dx,
                    offsetY: interactionRef.current.startPanY + dy
                }
            }));
        }
    };

    const handleTouchEnd = () => {
        interactionRef.current.isPinching = false;
        interactionRef.current.isPanning = false;
    };

    const imageStyle: React.CSSProperties = { 
        filter: `brightness(${edits.adjustments.brightness}%) contrast(${edits.adjustments.contrast}%) saturate(${edits.adjustments.saturate}%) blur(${edits.adjustments.blur}px)`, 
        transform: `scale(${edits.adjustments.zoom}) translate(${edits.adjustments.offsetX}px, ${edits.adjustments.offsetY}px)`,
        transition: interactionRef.current.isPinching || interactionRef.current.isPanning ? 'none' : 'transform 0.1s ease-out'
    };
    
    const shapeClass = previewShape === 'circle' ? 'rounded-full' : 'rounded-lg';
    const displayImageUrl = localImageUrl;

    return (
        <div className="fixed inset-0 h-full w-full flex flex-col bg-black text-white overflow-hidden z-[70]">
            <header className="absolute top-0 left-0 right-0 z-50 flex items-center justify-between p-4 pt-[max(1rem,env(safe-area-inset-top))] bg-gradient-to-b from-black/60 to-transparent pointer-events-none">
                <div className="pointer-events-auto">
                    <BackButton colorClass="text-white" text="" />
                </div>
                <h2 className="text-xl font-bold aurora-text">Post Editor</h2>
                <div className="flex gap-2 pointer-events-auto">
                    <button 
                        onClick={() => setAIModalOpen(true)}
                        className="bg-white/20 backdrop-blur-md text-white p-2 rounded-lg transition-all active:scale-95"
                    >
                        <i className="ph-duotone ph-magic-wand text-lg"></i>
                    </button>
                    <button onClick={handleSave} className="bg-gradient-to-r from-pink-500 to-yellow-500 text-white font-bold py-1.5 px-4 rounded-lg text-sm shadow-md transition-all active:scale-95">Save</button>
                </div>
            </header>

            {/* Main Preview Area - Centered and Fullscreen-ish */}
            <div className="flex-grow flex items-center justify-center relative p-4 overflow-hidden">
                <div className="w-full h-full flex items-center justify-center">
                    {postContext ? (
                        <div className="w-full max-w-sm relative transform scale-110 sm:scale-125">
                            <Suspense fallback={<div className="flex justify-center items-center h-64"><div className="spinner-white"></div></div>}>
                                <PostCard 
                                    puzzle={{ 
                                        ...postContext, 
                                        id: 'preview', 
                                        createdAt: TimestampValue.now(), 
                                        creatorId: userProfile?.uid || '', 
                                        creatorProfile: { 
                                            displayName: userProfile?.displayName || 'You', 
                                            photoURL: userProfile?.photoURL || '' 
                                        }, 
                                        imageUrl: displayImageUrl, 
                                        imageEdits: edits, 
                                        postColor: postContext.postColor || (userProfile?.primaryColor || DEFAULT_THEME_COLOR), 
                                        likes:[], dislikes:[], hearts:[], comments:[] 
                                    }} 
                                    isPreview={true} 
                                    hideComments={true}
                                    hideActions={true}
                                    hideHeader={true}
                                />
                                <div 
                                    className="absolute inset-0 z-10" 
                                    onTouchStart={handleTouchStart}
                                    onTouchMove={handleTouchMove}
                                    onTouchEnd={handleTouchEnd}
                                />
                            </Suspense>
                        </div>
                    ) : (
                        <div className={`w-full max-w-lg aspect-square overflow-hidden relative shadow-2xl ${shapeClass}`}>
                            <div className="w-full h-full">
                                <img src={displayImageUrl} alt="Preview" className="w-full h-full object-contain" style={imageStyle} />
                            </div>
                            <div 
                                className="absolute inset-0 z-20" 
                                onTouchStart={handleTouchStart}
                                onTouchMove={handleTouchMove}
                                onTouchEnd={handleTouchEnd}
                            />
                        </div>
                    )}
                </div>
            </div>

            {/* Bottom Controls Overlay */}
            {/* Bottom Controls Overlay */}
            <div className="flex-shrink-0 bg-gradient-to-t from-black/90 to-transparent p-6 pb-12 space-y-8 relative z-50">
                <div className="max-w-md mx-auto space-y-8">
                    <div className="space-y-8">
                        {/* Zoom Slider */}
                        <div className="space-y-3">
                            <div className="flex justify-between text-[10px] font-bold uppercase tracking-widest text-white/40">
                                <span>Zoom</span>
                                <span className="font-mono text-white/60">{edits.adjustments.zoom.toFixed(2)}x</span>
                            </div>
                            <div className="relative flex items-center">
                                <input 
                                    type="range" 
                                    min={0.5} 
                                    max={4} 
                                    step={0.01} 
                                    value={edits.adjustments.zoom} 
                                    onChange={v => setEdits(e => ({ ...e, adjustments: { ...e.adjustments, zoom: parseFloat(v.target.value) } }))} 
                                    className="w-full h-1.5 bg-white/10 rounded-full appearance-none cursor-pointer accent-pink-500" 
                                />
                            </div>
                        </div>

                        {/* Blur Slider */}
                        <div className="space-y-3">
                            <div className="flex justify-between text-[10px] font-bold uppercase tracking-widest text-white/40">
                                <span>Blur</span>
                                <span className="font-mono text-white/60">{edits.adjustments.blur.toFixed(1)}px</span>
                            </div>
                            <div className="relative flex items-center">
                                <input 
                                    type="range" 
                                    min={0} 
                                    max={20} 
                                    step={0.1} 
                                    value={edits.adjustments.blur} 
                                    onChange={v => setEdits(e => ({ ...e, adjustments: { ...e.adjustments, blur: parseFloat(v.target.value) } }))} 
                                    className="w-full h-1.5 bg-white/10 rounded-full appearance-none cursor-pointer accent-pink-500" 
                                />
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-center pt-2">
                        <button onClick={resetAdjustments} className="text-[10px] font-bold uppercase tracking-widest text-pink-400/80 hover:text-pink-400 transition-colors">
                            Reset All Adjustments
                        </button>
                    </div>
                </div>
            </div>
            
            <AIImageGeneratorModal 
                isOpen={isAIModalOpen}
                onClose={() => setAIModalOpen(false)}
                onImageGenerated={handleAIImageGenerated}
            />
        </div>
    );
};

export default ImageEditorPage;
